import classNames from 'classnames';
import React, { FC, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { TaskId } from 'api/models';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { FieldOption, StatusGroup, Task as TaskApi } from 'api/tasks';
import { MentionType } from 'components/molecules/mention';
import { TaskList } from 'components/molecules/tasks/task-list';
import { useDocuments } from 'components/organisms/document/document-store';
import { createDocTask, updateDocTask } from 'data/documents/actions/tasks';
import { EditorModes } from 'data/documents/constants';
import { useStatusField } from 'data/fields/hooks/account-fields';
import { Task } from 'data/tasks';
import { deleteTaskSagaAction, fetchTaskSaga } from 'data/tasks/actions';
import { deleteTask } from 'data/tasks/slice';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { MentionsDataParam } from 'vendor/ckeditor/plugins/mention/constants';
import styles from './doc-tasks-tab.module.css';

interface DocumentTasksProps {
  documentId: number;
  projectId: number;
  docTitle?: string;
  tasks: Task[];
  name?: string;
  documentSectionId?: number;
  disableNewRow?: boolean;
  isPrivate?: boolean;
  resourceSectionId?: number;
  renderExtraActions?: (task) => React.ReactNode;
  handleAddNewTask?: (
    taskName: string,
    meta: {
      resourceSectionId: number;
      documentSectionId: number;
      isPrivate: boolean;
    },
  ) => void;
}

export const DocumentTasksBase: FC<DocumentTasksProps> = ({
  documentId,
  projectId,
  docTitle,
  tasks,
  name,
  disableNewRow,
  resourceSectionId,
  documentSectionId,
  isPrivate,
  handleAddNewTask,
  renderExtraActions,
}) => {
  const dispatch = useDispatch();
  const statusField = useStatusField();
  const [docState] = useDocuments();

  // Test our socket update
  const handleSocketUpdate = useCallback(
    (socketData: SocketData<TaskApi>) => {
      if (
        socketData.data.source?.sourceType === 'DOCUMENT' &&
        socketData.data.source?.sourceId === documentId
      ) {
        const { data } = socketData;

        dispatch(
          fetchTaskSaga({
            projectId: socketData.projectId,
            taskId: data.taskId,
            fetchInBackground: true,
          }),
        );
      }
    },
    [dispatch, documentId],
  );

  const handleSocketDelete = useCallback(
    (socketData: SocketData<TaskApi>) => {
      const { data } = socketData;

      dispatch(deleteTask(TaskId(socketData.projectId, data.taskId)));
    },
    [dispatch],
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdate,
    EventType(Action.Create, ObjectType.Task),
  );
  useProjectWSUpdates(
    projectId,
    handleSocketUpdate,
    EventType(Action.Update, ObjectType.Task),
  );
  useProjectWSUpdates(
    projectId,
    handleSocketDelete,
    EventType(Action.Delete, ObjectType.Task),
  );

  const handleAssigneeChange = (taskId, selectedAssignee, resolveCallback) => {
    dispatch(
      updateDocTask({
        projectId: projectId as number,
        taskId,
        partialTask: {
          assignee: selectedAssignee,
        },
        options: {
          resolveCallback,
        },
      }),
    );
  };
  const handleDueDateChange = (taskId, payload) => {
    dispatch(
      updateDocTask({
        projectId: projectId as number,
        taskId,
        partialTask: payload,
      }),
    );
  };
  const handleDelete = (task: Task) => {
    dispatch(
      deleteTaskSagaAction({
        taskId: task.taskId,
        projectId: task.project,
      }),
    );
    const options = {
      taskId: `rl-task-${task.taskId}`,
    };

    if (docState.allEditors) {
      docState.allEditors[EditorModes.Private]?.execute(
        'removeTaskCommand',
        options,
      );
      docState.allEditors[EditorModes.Public]?.execute(
        'removeTaskCommand',
        options,
      );
    }
  };

  const url = window.location.href;
  const splitUrl = url.split('/');
  const spaceTabId = splitUrl[8];

  const linkToDoc = `<p>From the document: <a
  href="#"
  className="rl__mention"
  data-mention=${docTitle}
  ${MentionsDataParam.Type}=${MentionType.SpaceTab}
  ${MentionsDataParam.ProjectId}=${projectId}
  ${MentionsDataParam.ObjectId}=${spaceTabId}
>${docTitle}</a></p>`;

  const descriptionWithDocRef = `
  <blockquote>
    ${linkToDoc}
  </blockquote>
  `;
  const handleNewTaskAdded = (taskName: string) => {
    if (handleAddNewTask && documentSectionId && resourceSectionId) {
      handleAddNewTask(taskName, {
        resourceSectionId,
        documentSectionId,
        isPrivate: !!isPrivate,
      });
    } else {
      dispatch(
        createDocTask({
          documentId: documentId as number,
          projectId: projectId as number,
          taskName,
          resourceSectionId,
          taskDescription: descriptionWithDocRef,
        }),
      );
    }

    return Promise.resolve();
  };

  const handleStatusChange = (
    taskId: number,
    selected: FieldOption<StatusGroup>,
  ) => {
    if (statusField) {
      dispatch(
        updateDocTask({
          projectId: projectId as number,
          taskId,
          partialTask: {
            fields: [
              {
                fieldId: statusField.fieldId,
                fieldValue: selected.value,
              },
            ],
          },
        }),
      );
    }
  };
  const taskListRef = useRef<HTMLElement | null>(null);
  const navigateToTask = useNavigateToTask();

  const onItemClick = (task: TaskId) => {
    navigateToTask(task);
  };

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.wrapper__private]: isPrivate,
      })}
    >
      {name ? <h6>{name}</h6> : null}
      <TaskList
        size="small"
        onItemClick={onItemClick}
        refTo={taskListRef}
        disableNewRow={disableNewRow}
        data={tasks}
        renderExtraActions={renderExtraActions}
        handleStatusChange={handleStatusChange}
        handleAssigneeChange={handleAssigneeChange}
        handleDueDateChange={handleDueDateChange}
        handleDelete={handleDelete}
        handleNewTaskAdded={handleNewTaskAdded}
        projectId={projectId}
      />
    </div>
  );
};
